import { memo, useMemo, useState } from "react";
import { Box, Button, IconButton, InputBase, TextField, Tooltip, InputAdornment } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ResetIcon from "@mui/icons-material/RotateLeft";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import AppCard from "@plannel/components/ui/AppCard";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import StatPill from "@plannel/components/ui/StatPill";
import { FS } from "@plannel/utils/font-util";

/*
 * Measure 표시명(별칭) 설정 — 조밀 시트(dense sheet) 형태의 공용 컴포넌트.
 *
 * 화면마다 500px 짜리 2컬럼 AG Grid 로 measure/표시명을 편집하던 것을,
 * 한 화면에 다 보이는 4컬럼 28px 행 시트로 바꾼 것. 이름을 클릭하면 그 자리에서 인라인 편집한다.
 *   · 맞춤 이름이 지정된 항목은 앞에 primary 색 점(+옅은 링) + primary 색 텍스트(원래 이름은 tooltip).
 *   · 행에 마우스를 올리면 오른쪽에 rename 힌트가, 맞춤 항목은 되돌리기(✕)가 나타난다.
 *   · 이름을 비우고 확정해도 기본(시스템) 이름으로 되돌아간다.
 *   · 저장은 하지 않는다 — 값(displayNames)은 부모가 들고, 부모의 저장 버튼이 p13n 으로 upsert 한다.
 *
 * props
 *   measures     : string[]  measure 코드 목록(라벨은 t(code))
 *   displayNames : object    { [measureCd]: 맞춤이름 }  — 빈 문자열이면 기본 이름 사용
 *   onChange     : (next) => void   displayNames 전체를 교체해 돌려준다
 *   maxHeight    : 시트 본문 최대 높이(기본 420) — 넘으면 스크롤
 *   valueTypeByMeasure : object  { [measureCd]: valueTypeCd(QTY/AMT 등) } — 있으면 라벨이 같은
 *                        measure들의 구분 배지에 measureCd 대신 이 값을 보여준다(DP 전용, 없으면 기존처럼 cd 표시)
 */
const ROW_HEIGHT = 26;
const NAME_FONT_SIZE = FS.BODY;
const FILTER_ALL = "ALL";
const FILTER_CUSTOM = "CUSTOM";

const MeasureNameSettings = ({ measures, displayNames, onChange, valueTypeByMeasure, maxHeight = 420, sx }) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [filter, setFilter] = useState(FILTER_ALL);
  const [editing, setEditing] = useState(null); // { measure, draft }

  // displayNames 가 null 로 내려오는 경우(p13n 미저장)를 흡수한다. useMemo 안에서 쓰므로 참조를 고정.
  const names = useMemo(() => displayNames || {}, [displayNames]);

  // 표시 라벨 기준 정렬 — 95개(RP)까지 늘어나므로 알파벳 순이라야 훑어볼 수 있다.
  const rows = useMemo(() => {
    const list = (measures ?? []).map(measure => {
      const custom = (names[measure] || "").trim();
      return {
        measure,
        custom,
        systemLabel: t(measure),
        label: custom || t(measure),
        badge: valueTypeByMeasure?.[measure] || measure,
      };
    });
    // 서로 다른 measureCd가 우연히 같은 라벨로 번역된 경우(예: finAchievePerQty/finAchievePerAmt가
    // 둘 다 "재무계획 달성률(%)") 목록에서 구분이 안 된다 — 그 라벨만 코드를 같이 보여준다.
    const labelCounts = list.reduce((acc, row) => {
      const key = row.label.toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    for (const row of list) {
      row.ambiguous = labelCounts[row.label.toLowerCase()] > 1;
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [measures, names, t, valueTypeByMeasure]);

  const customCount = rows.filter(row => row.custom).length;

  const visibleRows = useMemo(() => {
    const word = keyword.trim().toLowerCase();
    return rows.filter(row => {
      if (filter === FILTER_CUSTOM && !row.custom) return false;
      if (!word) return true;
      return row.label.toLowerCase().includes(word) || row.systemLabel.toLowerCase().includes(word);
    });
  }, [rows, keyword, filter]);

  const commitEdit = () => {
    if (!editing) return;
    const { measure, draft } = editing;
    setEditing(null);
    const next = (draft || "").trim();
    if (next === (names[measure] || "").trim()) return;
    onChange({ ...names, [measure]: next });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(null);
    }
  };

  const handleRevert = (e, measure) => {
    e.stopPropagation();
    onChange({ ...names, [measure]: "" });
  };

  /*
   * 본문 높이는 "전체 목록" 기준으로 고정한다(필터 결과가 아니라).
   * 검색어를 칠 때마다 카드가 줄었다 늘었다 하면 아래 내용까지 들썩여 눈이 어지럽다.
   * 컬럼 수는 브레이크포인트마다 다르므로 높이도 같은 기준으로 계산해 sx 반응형 값으로 넘긴다.
   */
  const bodyHeight = cols => Math.min(maxHeight, Math.max(ROW_HEIGHT * 2, Math.ceil(rows.length / cols) * ROW_HEIGHT));

  return (
    <AppCard sx={{ width: "100%", maxWidth: 1120, ...sx }}>
      {/* 헤더 — 개수 요약 + 검색/필터/초기화 */}
      <Box
        sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
          px: 1.5, py: 1.25, borderBottom: 1, borderColor: "divider",
          background: theme =>
            `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0)} 45%)`,
        }}
      >
        <StatPill label={t("measure")} value={rows.length} />
        <StatPill label={t("custom")} value={customCount} accent={customCount > 0} />

        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          variant="outlined"
          value={keyword}
          placeholder={t("search")}
          onChange={e => setKeyword(e.target.value)}
          sx={{
            width: 190,
            "& .MuiInputBase-root": {
              height: 30, fontSize: FS.SUB, borderRadius: 999,
              bgcolor: "background.paper",
              transition: "box-shadow .15s ease",
              "&.Mui-focused": { boxShadow: theme => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}` },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
        <SegmentedControl
          value={filter}
          onChange={setFilter}
          options={[
            { value: FILTER_ALL, label: t("all") },
            { value: FILTER_CUSTOM, label: t("custom") },
          ]}
        />
        <Button size="small" startIcon={<ResetIcon />} onClick={() => onChange({})} disabled={!customCount}>
          {t("reset")}
        </Button>
      </Box>

      {/* 본문 — 4컬럼 조밀 시트 */}
      <Box
        sx={{
          height: { xs: bodyHeight(2), sm: bodyHeight(3), md: bodyHeight(4) },
          overflowY: "auto", overflowX: "hidden", px: 1, py: 1,
        }}
      >
        {visibleRows.length === 0 ? (
          <Box sx={{ py: 2.5, textAlign: "center", fontSize: FS.SUB, color: "text.disabled" }}>
            {t("MSG_NoData")}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              columnGap: 1.5,
            }}
          >
            {visibleRows.map(row => {
              if (editing?.measure === row.measure) {
                return (
                  <InputBase
                    key={row.measure}
                    autoFocus
                    value={editing.draft}
                    placeholder={row.systemLabel}
                    onChange={e => setEditing({ ...editing, draft: e.target.value })}
                    onKeyDown={handleKeyDown}
                    onBlur={commitEdit}
                    sx={{
                      height: ROW_HEIGHT, px: 1, fontSize: NAME_FONT_SIZE, borderRadius: 1.5,
                      bgcolor: "background.paper",
                      border: 1, borderColor: "primary.main",
                      boxShadow: theme => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }}
                  />
                );
              }
              return (
                <Tooltip
                  key={row.measure}
                  title={row.ambiguous ? `${row.custom ? row.systemLabel : row.label} (${row.badge})` : (row.custom ? row.systemLabel : row.label)}
                  placement="top"
                  disableInteractive
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditing({ measure: row.measure, draft: row.custom })}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEditing({ measure: row.measure, draft: row.custom });
                      }
                    }}
                    sx={{
                      position: "relative",
                      display: "flex", alignItems: "center", gap: 0.75,
                      height: ROW_HEIGHT, px: 1, borderRadius: 1.5,
                      cursor: "text", outline: "none", transition: "background-color .12s ease",
                      "&:hover, &:focus-visible": {
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                      },
                      "&:hover .measure-row-action, &:focus-visible .measure-row-action": { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 5, height: 5, flexShrink: 0, borderRadius: "50%",
                        bgcolor: row.custom ? "primary.main" : "transparent",
                        boxShadow: theme => (row.custom ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}` : "none"),
                      }}
                    />
                    {/*
                      * Typography 를 쓰지 않는다 — 설정 화면의 전역 SCSS(.content-main .MuiTypography-root)가
                      * font-size:16px 를 강제해 sx 의 글자 크기를 이겨버린다(injectFirst 미설정).
                      */}
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 1, minWidth: 0, fontSize: NAME_FONT_SIZE, lineHeight: 1.4,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        color: row.custom ? "primary.dark" : "text.primary",
                        fontWeight: row.custom ? 500 : 400,
                      }}
                    >
                      {row.label}
                    </Box>
                    {/*
                      * 서로 다른 measureCd가 우연히 같은 라벨로 번역된 경우 목록에서 구분이 안 된다 —
                      * 그 라벨끼리만 구분값을 같이 보여준다(번역 파일은 건드리지 않음).
                      * valueTypeByMeasure가 있으면(DP) valueTypeCd(QTY/AMT 등)를, 없으면(IP/RP) measureCd를 보여준다.
                      */}
                    {row.ambiguous && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0, fontSize: FS.CAPTION, lineHeight: 1.4,
                          px: 0.5, borderRadius: 0.75,
                          color: "warning.dark", bgcolor: theme => alpha(theme.palette.warning.main, 0.12),
                        }}
                      >
                        {row.badge}
                      </Box>
                    )}
                    {/*
                      * 이름을 바꾼 항목은 원래 이름을 옆에 같이 둔다 —
                      * 맞춤 이름만 보이면 어떤 measure 였는지 알 수 없다(DP Workbench Measure 와 같은 표기).
                      */}
                    {row.custom && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 1, minWidth: 0, fontSize: NAME_FONT_SIZE, lineHeight: 1.4,
                          fontStyle: "italic", color: "text.disabled",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        ({row.systemLabel})
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1, minWidth: 16 }} />
                    {/* 되돌리기/편집 표시는 절대 위치 — 행 폭을 차지하지 않아 hover 시 이름이 밀리지 않는다. */}
                    {row.custom ? (
                      <IconButton
                        className="measure-row-action"
                        size="small"
                        onClick={e => handleRevert(e, row.measure)}
                        sx={{
                          position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)",
                          p: 0.25, opacity: 0, transition: "opacity .12s ease", color: "text.disabled",
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    ) : (
                      <EditIcon
                        className="measure-row-action"
                        sx={{
                          position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)",
                          fontSize: FS.SUB, color: "text.disabled", opacity: 0, transition: "opacity .12s ease",
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      {/* 푸터 — 사용법 안내 */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 0.75,
          px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider", bgcolor: "grey.50",
        }}
      >
        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />
        <Box component="span" sx={{ fontSize: FS.BODY, color: "text.disabled" }}>{t("MSG_ClickMeasureNameToRename")}</Box>
      </Box>
    </AppCard>
  );
};

// 부모(DpSettings 등)가 그리드 편집·검색 같은 무관한 상태로 자주 리렌더되는 화면이라, props가 실제로
// 안 바뀌었으면(참조 동일) 이 무거운 목록까지 같이 다시 그리지 않도록 memo로 막는다.
export default memo(MeasureNameSettings);
